import { useQuery } from '@tanstack/react-query'
import { AlertCircle, ClipboardCheck } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { api } from '../api'
import type { CoachCardStats, CoachCheckStats, CoachExample, CoachScorecard, NoCalls } from '../types'

// The coach card grader's numbers (GET /api/coach-scorecard): per day, per BDR and per model.
// Card lines and their classes only -- the API holds no heard line, so this page shows none.
const NO_CALLS: NoCalls = 'no calls'
const PCTS = [['RIGHT_MOVE', 'Right'], ['WRONG_MOVE', 'Wrong'], ['BANNED', 'Banned'],
  ['TOO_LONG', 'Too long'], ['EMPTY', 'Empty'], ['dropped', 'Dropped']] as const
const STEPS = ['words', 'model', 'card', 'total'] as const
const CARD_COLS = ['Cards', ...PCTS.map(([, label]) => `% ${label}`)]
const CHECK_COLS = ['Calls', ...STEPS.map((s) => `${s} p50 / p90 ms`), 'Owner off-role']
const KEYS_COL = 'Keys right / wrong / ?'
const TD = 'px-3 py-2 align-top'

// The grader's days are UTC days (the live coach files a call under its UTC start date).
function utcYesterday(): string {
  return new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
}

function lastRun(run: CoachScorecard['last_run']): string {
  if (!run) return 'not graded yet'
  const at = new Date(run.at)
  const time = Number.isNaN(at.getTime())
    ? run.at
    : at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
  return `graded ${time}, ${run.status.toUpperCase()}`
}

function NoCallsCell({ span }: { span: number }) {
  return <td colSpan={span} className={`${TD} text-slate-500`}>{NO_CALLS}</td>
}

function CardCells({ g }: { g: CoachCardStats | NoCalls }) {
  if (g === NO_CALLS) return <NoCallsCell span={CARD_COLS.length} />
  const examples = g.examples as Record<string, CoachExample[] | undefined>
  return (
    <>
      <td className={`${TD} font-mono`}>{g.cards}</td>
      {PCTS.map(([k]) => (
        <td key={k} className={`${TD} font-mono`}>
          {g.pct[k]}%
          {(examples[k] ?? []).map((e, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: the API's own newest-first order
            <div key={i} className="mt-1 w-56 font-sans text-xs">
              <span className="text-slate-200">{`"${e.card}"`}</span>{' '}
              <span className="text-slate-500">{`${e.class}: ${e.why}`}</span>
            </div>
          ))}
        </td>
      ))}
    </>
  )
}

function CheckCells({ g }: { g: CoachCheckStats | NoCalls }) {
  if (g === NO_CALLS) return <NoCallsCell span={CHECK_COLS.length + 1} />
  const ms = (v: number | null) => (v === null ? '--' : v)
  return (
    <>
      <td className={`${TD} font-mono`}>{g.calls}</td>
      {STEPS.map((s) => (
        <td key={s} className={`${TD} whitespace-nowrap font-mono text-xs`}>
          {ms(g.lag_ms[s].p50)} / {ms(g.lag_ms[s].p90)}
        </td>
      ))}
      <td className={`${TD} font-mono`}>{g.owner_off_role ?? '--'}</td>
      <td className={`${TD} whitespace-nowrap font-mono`}>
        {g.key_check.right} / {g.key_check.wrong} / {g.key_check.unknown}
      </td>
    </>
  )
}

function Table({ cols, children }: { cols: string[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-800 bg-slate-900/40">
      <table className="w-full text-sm">
        <thead className="bg-slate-900/80 text-xs text-slate-400">
          <tr>
            {cols.map((c) => <th key={c} className="px-3 py-2 text-left">{c}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

export function CoachScorecardPage() {
  const [range, setRange] = useState(() => ({ since: utcYesterday(), until: utcYesterday() }))
  const { data, isLoading, error } = useQuery({
    queryKey: ['coach-scorecard', range.since, range.until],
    queryFn: () => api.coachScorecard(range.since, range.until),
  })

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-2">
        <ClipboardCheck className="h-5 w-5 text-indigo-400" />
        <h1 className="text-xl font-semibold">Coach scorecard</h1>
        <span className="ml-2 text-xs text-slate-500">{data ? lastRun(data.last_run) : '...'}</span>
      </header>

      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-sm">
        {(['since', 'until'] as const).map((k) => (
          // biome-ignore lint/a11y/noLabelWithoutControl: implicit-label, child input
          <label key={k} className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wide text-slate-500">
              {k === 'since' ? 'From' : 'To'}
            </span>
            <input
              type="date"
              value={range[k]}
              onChange={(e) => e.target.value && setRange({ ...range, [k]: e.target.value })}
              className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-200"
            />
          </label>
        ))}
        <span className="text-xs text-slate-500">UTC days, at most 31</span>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-900 bg-red-950/40 p-3 text-sm text-red-200">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <span>{(error as Error).message}</span>
        </div>
      )}
      {isLoading && <div className="h-24 animate-pulse rounded-lg bg-slate-800/60" />}

      {data?.days.map((d) => (
        <section key={d.day} className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-300">{d.day}</h2>
          {d.by_bdr.length === 0 ? (
            <div className="rounded-lg border border-slate-800 p-4 text-sm text-slate-500">
              {NO_CALLS}
            </div>
          ) : (
            <Table cols={['BDR', ...CARD_COLS, ...CHECK_COLS, KEYS_COL]}>
              {d.by_bdr.map((g) => (
                <tr key={g.bdr} className="border-t border-slate-800">
                  <td className={`${TD} font-semibold`}>{g.bdr}</td>
                  <CardCells g={g.cards} />
                  <CheckCells g={g.checks} />
                </tr>
              ))}
            </Table>
          )}
          {d.by_model.length > 0 && (
            <Table cols={['Model', ...CARD_COLS]}>
              {d.by_model.map((g) => (
                <tr key={g.model} className="border-t border-slate-800">
                  <td className={`${TD} font-semibold`}>{g.model}</td>
                  <CardCells g={g.cards} />
                </tr>
              ))}
            </Table>
          )}
        </section>
      ))}
    </div>
  )
}
